import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { confirmDialog } from '../../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../../utils/notify';

const DISCIPLINES = ['Architect', 'Structural', 'MEP', 'QS', 'Geotechnical', 'Other'];
const empty = () => ({
  name: '', discipline: 'Architect', company: '', email: '', mobile: '', notes: '',
  portalAccessType: 'DIRECTORY_ONLY',
});

const UsersAccessConsultants = ({ initialMode }) => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(initialMode === 'add');

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([adminAPI.getConsultants(), adminAPI.getUsers()]);
    setRows(c?.data?.data || c?.data || []);
    setUsers(u?.data?.data || u?.data || []);
  }, []);

  useEffect(() => { load().catch(() => notifyError('Unable to load consultants')); }, [load]);

  const save = async () => {
    if (!form.name.trim()) {
      notifyError('Name is required');
      return;
    }
    try {
      if (editingId) {
        await adminAPI.updateConsultant(editingId, {
          name: form.name,
          discipline: form.discipline,
          company: form.company,
          email: form.email,
          mobile: form.mobile,
        });
        notifySuccess('Consultant updated');
      } else {
        await adminAPI.createConsultant({
          name: form.name,
          discipline: form.discipline,
          company: form.company,
          email: form.email,
          mobile: form.mobile,
        });
        if (form.portalAccessType === 'CREATE_PORTAL_USER' && form.email) {
          try {
            await adminAPI.provisionUser({
              name: form.name,
              email: form.email,
              mobile: form.mobile,
              accountType: 'CONSULTANT_PORTAL',
              role_codes: ['CONSULTANT'],
              sendInvitation: true,
              projectAssignments: [],
            });
            notifySuccess('Consultant created and portal user invited');
          } catch (err) {
            notifyError(err?.response?.data?.message || 'Directory created; portal user failed');
          }
        } else {
          notifySuccess('Consultant created');
        }
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty());
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Save failed');
    }
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      discipline: row.discipline || 'Architect',
      company: row.company || '',
      email: row.email || '',
      mobile: row.mobile || '',
      notes: '',
      portalAccessType: 'DIRECTORY_ONLY',
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!(await confirmDialog('Delete this consultant?', 'Delete'))) return;
    try {
      await adminAPI.deleteConsultant(id);
      notifySuccess('Deleted');
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Delete failed');
    }
  };

  const portalBadge = (row) => {
    const match = users.find((u) => String(u.email || '').toLowerCase() === String(row.email || '').toLowerCase());
    if (!match) return <span className="ua-badge">Directory Only</span>;
    if (match.invitationStatus === 'SETUP_PENDING' || match.invitationStatus === 'FAILED') {
      return <span className="ua-badge ua-badge--warn">Invitation Pending</span>;
    }
    if (match.accountStatus === 'INACTIVE') return <span className="ua-badge">Disabled</span>;
    return <span className="ua-badge ua-badge--ok">Portal User</span>;
  };

  return (
    <div>
      <div className="filter-bar">
        <button type="button" className="btn-primary" onClick={() => { setEditingId(null); setForm(empty()); setOpen(true); }}>Add Consultant</button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Discipline</th><th>Company</th><th>Email</th><th>Mobile</th><th>Access</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td><span className="category-tag">{row.discipline || '—'}</span></td>
                <td>{row.company || '—'}</td>
                <td>{row.email || '—'}</td>
                <td>{row.mobile || '—'}</td>
                <td>{portalBadge(row)}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => onEdit(row)}>Edit</button>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => onDelete(row.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="ua-drawer-overlay" onClick={() => setOpen(false)}>
          <div className="ua-drawer" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>{editingId ? 'Edit Consultant' : 'Add Consultant'}</h2>
            <div className="form-group"><label>Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-group">
              <label>Discipline</label>
              <select value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })}>
                {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Company</label><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div className="form-group"><label>Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-group"><label>Mobile</label><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            {!editingId && (
              <div className="form-group">
                <label>Portal Access Type</label>
                <select value={form.portalAccessType} onChange={(e) => setForm({ ...form, portalAccessType: e.target.value })}>
                  <option value="DIRECTORY_ONLY">Directory Only</option>
                  <option value="CREATE_PORTAL_USER">Create Consultant Portal User</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAccessConsultants;
